/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { AppConfig } from '../types/app';
import { RootInitiativeData, Subscription, SubscriptionStatus } from '../types/initiative';

export enum ProductCodes {
  SGXESGenome = 'company_tracker_light',
  CompanyTrackerStarter = 'company_tracker_starter',
  CompanyTracker = 'company_tracker',
  CompanyTrackerPro = 'company_tracker_pro',
  CompanyTrackerEnterprise = 'company_tracker_enterprise',
  PortfolioTracker = 'portfolio_tracker',
}

export const getProductType = (productCode: string) => {
  switch (productCode) {
    case ProductCodes.CompanyTrackerStarter:
      return 'Company Tracker Starter';
    case ProductCodes.SGXESGenome:
    case ProductCodes.CompanyTracker:
      return 'Company Tracker';
    case ProductCodes.CompanyTrackerPro:
      return 'Company Tracker Pro';
    case ProductCodes.CompanyTrackerEnterprise:
      return 'Company Tracker Enterprise';
    case ProductCodes.PortfolioTracker:
      return 'Portfolio Tracker';
    default:
      return '';
  }
};

export const permissionGroupOptions = [
  { code: 'free', label: 'Free (limit 1 survey)' },
  { code: 'company_tracker_light', label: 'Company Tracker (aka Light)' },
  { code: 'company_tracker_pro', label: 'Company Tracker Pro' },
  { code: 'premium_unlimited', label: 'Company Tracker Enterprise' },
  { code: 'premium_unlimited_portfolio_tracker', label: 'Portfolio Tracker + Company Tracker Enterprise' },
  { code: 'premium_trial_limit1', label: 'Company Tracker Pro (Legacy 1)' },
  { code: 'premium_limit5', label: 'Company Tracker Pro (Legacy 2)' },
  { code: 'premium_limit20', label: 'Company Tracker Pro (Legacy 3)' },
  { code: 'portfolio_tracker', label: 'Portfolio Tracker' },
  { code: 'portfolio_tracker_exchange', label: 'Portfolio Tracker Exchange' },
];

type GlobalDataCheck = {
  organization?: RootInitiativeData;
  appConfig?: AppConfig;
};

export class SubscriptionService {

  public static hasActive(productCode: ProductCodes, subscriptions?: Subscription[]) {
    return SubscriptionService.hasStatusForProduct(productCode, 'active', subscriptions);
  }

  public static hasTrial(productCode: ProductCodes, subscriptions?: Subscription[]) {
    return SubscriptionService.hasStatusForProduct(productCode, 'trialing', subscriptions);
  }

  public static getBestValidSubscription(productCode: ProductCodes, subscriptions?: Subscription[]) {
    if (!subscriptions) {
      return;
    }

    const priority = SubscriptionService.useableSubscriptions()
    return SubscriptionService.getSubscriptionByPriority(productCode, priority, subscriptions);
  }

  public static getBestFromAllSubscription(productCode: ProductCodes, subscriptions?: Subscription[]) {
    const priority = SubscriptionService.allStatusByPriority()
    return SubscriptionService.getSubscriptionByPriority(productCode, priority, subscriptions);
  }

  private static getSubscriptionByPriority(productCode: ProductCodes, priority: SubscriptionStatus[], subscriptions?: Subscription[]) {
    if (!subscriptions) {
      return;
    }

    for (const status of priority) {
      const sub = SubscriptionService.getStatusForProduct(productCode, status, subscriptions)
      if (sub) {
        return sub;
      }
    }
  }

  public static shouldAllowAccess(appConfig: Pick<AppConfig, 'validProductCodes'>, subscriptions?: Subscription[]) {
    if (!subscriptions) {
      // This is not an open app, so you must have some subscription
      return false;
    }

    const validProductCodes: string[] = appConfig.validProductCodes;
    return subscriptions.some(sub => {
      return ['active', 'trialing'].some(s => s === sub.status)
        && sub.items.some(item => item.productCode && validProductCodes.includes(item.productCode));
    });
  }

  public static hasUpgradeableSub(
    appConfig: Pick<AppConfig, 'validProductCodes' | 'productCode'> | undefined,
    subscriptions: Subscription[] | undefined = []
  ) {

    return false; // Nothing is upgradeable for now
    //
    // if (!appConfig) {
    //   return false;
    // }
    //
    // // Upgrade limited to CT Starter for now
    // if (appConfig.productCode !== ProductCodes.CompanyTrackerStarter) {
    //   return false
    // }
    //
    // const validSubs = subscriptions.filter(s => s.paymentProvider === 'stripe');
    // if (validSubs.length === 0) {
    //   // This is not an open app, so you must have some subscription
    //   return false;
    // }
    //
    // const validProductCodes: string[] = appConfig.validProductCodes;
    // return validSubs.some(sub => {
    //   return ['active', 'trialing'].some(s => s === sub.status)
    //     && sub.items.some(item => item.productCode && validProductCodes.includes(item.productCode));
    // });
  }


  /**
   * Subscription status by priority
   * @private
   */
  private static useableSubscriptions(): SubscriptionStatus[] {
    return [
      'active', // Fine
      'trialing', // Trial is running

      // Need user interaction, block access
      'past_due',
      'unpaid',
      'incomplete',
    ]
  }

  private static allStatusByPriority(): SubscriptionStatus[] {
    return [
      ...SubscriptionService.useableSubscriptions(),
      // Not recoverable, block access, require new subscription
      'canceled',
      'incomplete_expired',
    ];
  }

  private static hasStatusForProduct(
    productCode: ProductCodes,
    status: SubscriptionStatus,
    subscriptions?: Subscription[]
  ): boolean {
    if (!subscriptions) {
      return false;
    }

    return subscriptions.some(sub => {
      return sub.status === status && sub.items.some(item => item.productCode === productCode);
    });
  }

  private static getStatusForProduct(
    productCode: ProductCodes,
    status: SubscriptionStatus,
    subscriptions?: Subscription[]
  ): Subscription | undefined {

    if (!subscriptions) {
      return undefined;
    }

    const subs = subscriptions.filter(sub => {
      return sub.status === status && sub.items.some(item => item.productCode === productCode);
    });

    // Sort by periodEnd
    return subs.sort((a, b) => b.periodEnd - a.periodEnd)[0];
  }

  public static shouldAddTrialPaymentMethod = ({ organization, appConfig }: GlobalDataCheck) => {
    if (!organization || organization.customer?.defaultPaymentMethod || !appConfig?.productCode) {
      return false;
    }

    return SubscriptionService.hasTrial(appConfig.productCode, organization.calculatedSubscriptions);
  }

  public static canManageSubscriptions({ appConfig }: Required<Pick<GlobalDataCheck, 'appConfig'>>) {
    return appConfig.productCode !== ProductCodes.CompanyTrackerStarter;
  }
}
